import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "accepted",
    "card",
    "displayedOdds",
    "fixture",
    "form",
    "formAmount",
    "history",
    "message",
    "modal",
    "modalEvent",
    "modalMessage",
    "modalLimitMessage",
    "modalRange",
    "modalOdds",
    "modalOutcome",
    "modalPayout",
    "modalStakeInput",
    "modalSubmit",
    "modalUseMax",
    "navigationLoading",
    "odds",
    "price",
    "stakeInput",
    "status",
    "submit"
  ]
  static values = {
    quoteUrl: String,
    wsUrl: String,
    assetIds: Array,
    edgeBps: Number,
    slippageBps: Number,
    liquiditySafetyBps: Number,
    locale: String,
    translations: Object
  }

  connect() {
    this.subscribedAssetIds = new Set()
    this.orderBooks = new Map()
    this.connected = true
    this.quoteRefreshing = false

    if (this.hasModalTarget) {
      this.boundResetBetModal = this.resetBetModal.bind(this)
      this.modalTarget.addEventListener("hidden.bs.modal", this.boundResetBetModal)
    }

    if (!this.hasWsUrlValue || this.assetIdsValue.length === 0 || !window.WebSocket) return

    this.openSocket()
  }

  disconnect() {
    this.connected = false
    this.stopQuoteRefresh()
    this.stopSocketHeartbeat()
    if (this.reconnectTimer) window.clearTimeout(this.reconnectTimer)
    if (this.socket) this.socket.close()
    if (this.hasModalTarget && this.boundResetBetModal) {
      this.modalTarget.removeEventListener("hidden.bs.modal", this.boundResetBetModal)
    }
  }

  openSocket() {
    this.socket = new WebSocket(this.wsUrlValue)
    this.socket.addEventListener("open", () => {
      this.setStatus(this.t("live"))
      this.startSocketHeartbeat()
      this.assetIdsValue.forEach((assetId) => this.subscribeAsset(assetId))
      this.cardTargets.forEach((card) => this.subscribeAsset(card.dataset.assetId))
    })
    this.socket.addEventListener("message", (event) => this.handleMessage(event))
    this.socket.addEventListener("close", () => {
      this.setStatus(this.t("offline"))
      this.stopSocketHeartbeat()
      this.scheduleReconnect()
    })
    this.socket.addEventListener("error", () => this.setStatus(this.t("offline")))
  }

  startSocketHeartbeat() {
    this.stopSocketHeartbeat()
    this.socketHeartbeat = window.setInterval(() => {
      if (this.socket?.readyState === WebSocket.OPEN) this.socket.send("PING")
    }, 10000)
  }

  stopSocketHeartbeat() {
    if (this.socketHeartbeat) window.clearInterval(this.socketHeartbeat)
    this.socketHeartbeat = null
  }

  scheduleReconnect() {
    if (!this.connected || this.reconnectTimer) return

    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null
      this.subscribedAssetIds.clear()
      this.openSocket()
    }, 2000)
  }

  cardTargetConnected(card) {
    this.subscribeAsset(card.dataset.assetId)
  }

  subscribeAsset(assetId) {
    if (!this.subscribedAssetIds) this.subscribedAssetIds = new Set()
    if (!window.WebSocket) return

    const id = String(assetId || "")
    if (id === "" || this.subscribedAssetIds.has(id)) return
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) return

    this.socket.send(JSON.stringify({
      assets_ids: [id],
      type: "market",
      custom_feature_enabled: true
    }))
    this.subscribedAssetIds.add(id)
  }

  handleMessage(event) {
    if (event.data === "PONG") return

    let payload
    try {
      payload = JSON.parse(event.data)
    } catch (_error) {
      return
    }

    const messages = Array.isArray(payload) ? payload : [payload]
    messages.forEach((message) => this.applyMarketMessage(message))
  }

  applyMarketMessage(message) {
    if (message.event_type === "book") {
      this.replaceOrderBook(message.asset_id, message.asks)
      const bestAsk = this.bestAsk(message.asset_id)
      if (bestAsk) this.updateAsset(message.asset_id, bestAsk)
      this.updateSelectedLimitFromBook(message.asset_id)
      return
    }

    if (message.event_type === "best_bid_ask") {
      this.updateAsset(message.asset_id, message.best_ask)
      return
    }

    if (message.event_type === "price_change") {
      ;(message.price_changes || []).forEach((change) => {
        this.applyOrderBookChange(change)
        if (change.best_ask) this.updateAsset(change.asset_id, change.best_ask)
        this.updateSelectedLimitFromBook(change.asset_id)
      })
    }
  }

  replaceOrderBook(assetId, asks) {
    const id = String(assetId || "")
    if (id === "") return

    const levels = new Map()
    ;(asks || []).forEach((row) => {
      const price = Number(row.price)
      const size = Number(row.size)
      if (Number.isFinite(price) && price > 0 && Number.isFinite(size) && size > 0) {
        levels.set(String(row.price), { price, size })
      }
    })
    this.orderBooks.set(id, levels)
  }

  applyOrderBookChange(change) {
    if (String(change.side || "").toUpperCase() !== "SELL") return

    const id = String(change.asset_id || "")
    const price = Number(change.price)
    const size = Number(change.size)
    if (id === "" || !Number.isFinite(price) || price <= 0 || !Number.isFinite(size)) return

    const levels = this.orderBooks.get(id) || new Map()
    const key = String(change.price)
    if (size <= 0) {
      levels.delete(key)
    } else {
      levels.set(key, { price, size })
    }
    this.orderBooks.set(id, levels)
  }

  bestAsk(assetId) {
    const levels = this.orderBooks.get(String(assetId || ""))
    if (!levels || levels.size === 0) return null

    return Math.min(...Array.from(levels.values(), (level) => level.price))
  }

  updateAsset(assetId, rawPrice) {
    const price = Number(rawPrice)
    if (!assetId || !Number.isFinite(price) || price <= 0 || price >= 1) return

    this.cardTargets
      .filter((card) => card.dataset.assetId === String(assetId))
      .forEach((card) => this.updateCard(card, price))
  }

  updateCard(card, price) {
    const priceTarget = this.priceTargets.find((target) => card.contains(target))
    const oddsTarget = this.oddsTargets.find((target) => card.contains(target))
    const nextOdds = this.customerOdds(price)
    if (priceTarget) priceTarget.textContent = `${(price * 100).toFixed(1)}¢`
    if (oddsTarget) oddsTarget.textContent = nextOdds.toFixed(2)
    card.dataset.odds = String(nextOdds)
    this.setDisplayedOdds(card.closest("form"), nextOdds)

    if (this.selectedForm && this.selectedForm.contains(card)) {
      this.selectedOdds = nextOdds
      this.renderModalOdds()
      this.updateBetPreview()
    }
  }

  customerOdds(price) {
    const validatedPrice = Math.min(Math.max(price, 0.000001), 0.999999)
    const marginMultiplier = 1 - this.edgeBpsValue / 10000
    return (1 / validatedPrice) * marginMultiplier
  }

  setStatus(label) {
    if (this.hasStatusTarget) this.statusTarget.textContent = label
  }

  showNavigationLoading(event) {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return

    this.element.classList.add("is-navigating")
    this.element.setAttribute("aria-busy", "true")
    if (this.hasNavigationLoadingTarget) this.navigationLoadingTarget.hidden = false
  }

  openBetModal(event) {
    event.preventDefault()

    const button = event.currentTarget
    const form = button.closest("form")
    if (!form || !this.hasModalTarget || !window.bootstrap?.Modal) return

    this.selectedForm = form
    this.selectedAssetId = String(button.dataset.assetId || "")
    this.selectedOdds = Number(button.dataset.odds || this.targetInside(this.oddsTargets, button)?.textContent || 0)
    this.selectedMinStake = 1
    this.selectedMaxStake = null
    this.selectedNonBookMax = null

    if (this.hasModalEventTarget) this.modalEventTarget.textContent = this.fieldValue(form, "event")
    if (this.hasModalOutcomeTarget) this.modalOutcomeTarget.textContent = this.fieldValue(form, "display_outcome") || this.fieldValue(form, "outcome")
    this.renderModalOdds()

    if (this.hasModalStakeInputTarget) {
      this.modalStakeInputTarget.value = ""
      this.modalStakeInputTarget.min = "1"
      this.modalStakeInputTarget.removeAttribute("max")
      window.setTimeout(() => this.modalStakeInputTarget.focus(), 200)
    }
    this.renderModalLimits()
    this.updateBetPreview()
    this.setModalMessage("")

    this.betModalInstance().show()
    this.refreshSelectedQuote(form)
    this.startQuoteRefresh(form)
  }

  updateBetPreview() {
    if (!this.hasModalPayoutTarget) return

    const amount = Number(this.hasModalStakeInputTarget ? this.modalStakeInputTarget.value : 0)
    const odds = Number(this.selectedOdds || 0)
    const payout = Number.isFinite(amount) && amount > 0 && Number.isFinite(odds) && odds > 0 ? amount * odds : 0
    this.modalPayoutTarget.textContent = `${this.formatTp(payout)} TP`
    this.renderModalValidation()
  }

  useMaximumStake() {
    if (!this.hasModalStakeInputTarget || !Number.isFinite(this.selectedMaxStake)) return

    this.modalStakeInputTarget.value = this.selectedMaxStake.toFixed(2)
    this.updateBetPreview()
    this.modalStakeInputTarget.focus()
  }

  submitModalOrder(event) {
    event.preventDefault()
    if (!this.selectedForm || !this.hasModalStakeInputTarget) return

    this.placeOrder({
      form: this.selectedForm,
      stakeInput: this.modalStakeInputTarget,
      button: this.hasModalSubmitTarget ? this.modalSubmitTarget : event.currentTarget,
      useModalMessage: true
    })
  }

  async submitOrder(event) {
    event.preventDefault()

    const form = event.currentTarget
    if (!(form instanceof HTMLFormElement) || form.dataset.pending === "true") return

    const button = this.targetInside(this.submitTargets, form) || event.submitter || form.querySelector("button[type='submit']")
    const fixture = form.closest("[data-polymarket-feed-target~='fixture']")
    const stakeInput = this.targetInside(this.stakeInputTargets, fixture) || this.targetInside(this.stakeInputTargets, form)

    this.placeOrder({ form, stakeInput, button })
  }

  async placeOrder({ form, stakeInput, button, useModalMessage = false }) {
    if (!(form instanceof HTMLFormElement) || form.dataset.pending === "true") return
    if (useModalMessage && !this.modalAmountValid()) return

    const formAmount = this.targetInside(this.formAmountTargets, form)
    if (formAmount && stakeInput) formAmount.value = stakeInput.value

    this.setFormMessage(form, "")
    if (useModalMessage) this.setModalMessage("")
    this.setSubmitting(form, button, true)

    try {
      const response = await fetch(form.action, {
        method: form.method.toUpperCase(),
        body: new FormData(form),
        credentials: "same-origin",
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfToken(),
          "X-Requested-With": "XMLHttpRequest"
        }
      })
      const payload = await this.parseJson(response)

      if (!response.ok || payload.ok === false) {
        this.applyQuoteUpdate(form, payload.quote)
        throw new Error(payload.message || this.t("bet_failed"))
      }

      if (stakeInput) stakeInput.value = ""
      this.updateBalances(payload.balance)
      this.setFormMessage(form, payload.message || this.t("bet_accepted"), "success")
      this.setAcceptedSummary(form, payload.order)
      this.prependHistory(payload.order)
      this.showToast(payload.message || this.t("bet_accepted"), "success")
      if (useModalMessage) this.closeBetModal()
    } catch (error) {
      const message = error.message || this.t("bet_failed")
      if (useModalMessage) {
        this.setModalMessage(message)
      } else {
        this.setFormMessage(form, message, "danger")
      }
    } finally {
      this.setSubmitting(form, button, false)
    }
  }

  async refreshSelectedQuote(form, { silent = false } = {}) {
    if (!this.hasQuoteUrlValue || !(form instanceof HTMLFormElement) || this.quoteRefreshing) return

    const button = this.hasModalSubmitTarget ? this.modalSubmitTarget : undefined
    this.quoteRefreshing = true
    this.updateModalSubmitAvailability()
    if (!silent) this.setModalMessage(this.t("checking_limits"))

    try {
      const response = await fetch(this.quoteUrlValue, {
        method: "POST",
        body: new FormData(form),
        credentials: "same-origin",
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfToken(),
          "X-Requested-With": "XMLHttpRequest"
        }
      })
      const payload = await this.parseJson(response)
      if (!response.ok || payload.ok === false) {
        throw new Error(payload.message || this.t("odds_unavailable"))
      }

      this.applyQuoteUpdate(form, payload.quote)
      if (!silent) this.setModalMessage("")
    } catch (error) {
      if (!silent) this.setModalMessage(error.message || this.t("odds_unavailable"))
    } finally {
      this.quoteRefreshing = false
      this.updateModalSubmitAvailability()
    }
  }

  startQuoteRefresh(form) {
    this.stopQuoteRefresh()
    this.quoteRefreshTimer = window.setInterval(() => {
      if (this.selectedForm === form) this.refreshSelectedQuote(form, { silent: true })
    }, 3000)
  }

  stopQuoteRefresh() {
    if (this.quoteRefreshTimer) window.clearInterval(this.quoteRefreshTimer)
    this.quoteRefreshTimer = null
  }

  applyQuoteUpdate(form, quote) {
    if (!(form instanceof HTMLFormElement) || !quote) return

    const odds = Number(quote.customer_odds)
    if (!Number.isFinite(odds) || odds <= 0) return

    const card = this.targetInside(this.cardTargets, form)
    const oddsTarget = card ? this.targetInside(this.oddsTargets, card) : undefined
    if (oddsTarget) oddsTarget.textContent = odds.toFixed(2)
    if (card) card.dataset.odds = String(odds)
    this.setDisplayedOdds(form, odds)

    if (this.selectedForm === form) {
      this.selectedOdds = odds
      this.applyBetLimits(quote)
      this.renderModalOdds()
      this.updateBetPreview()
    }
  }

  applyBetLimits(quote) {
    const minimum = Number(quote.min_stake_tp)
    const maximum = Number(quote.max_stake_tp)

    this.selectedMinStake = Number.isFinite(minimum) ? minimum : 1
    this.selectedMaxStake = Number.isFinite(maximum) ? maximum : null
    this.selectedNonBookMax = quote.non_book_limit_applies === true && Number.isFinite(maximum)
      ? maximum
      : Number.POSITIVE_INFINITY
    this.renderModalLimits()
  }

  updateSelectedLimitFromBook(assetId) {
    if (!this.selectedForm || String(assetId || "") !== this.selectedAssetId) return
    if (this.selectedNonBookMax === null) return

    const bookMaximum = this.realtimeBookMaximum(assetId)
    if (!Number.isFinite(bookMaximum)) return

    this.selectedMaxStake = Math.min(bookMaximum, this.selectedNonBookMax)
    this.renderModalLimits()
    this.renderModalValidation()
  }

  realtimeBookMaximum(assetId) {
    const levels = this.orderBooks.get(String(assetId || ""))
    const bestAsk = this.bestAsk(assetId)
    if (!levels || !Number.isFinite(bestAsk)) return null

    const worstPrice = bestAsk * (1 + this.slippageBpsValue / 10000)
    const notional = Array.from(levels.values()).reduce((sum, level) => {
      return level.price <= worstPrice ? sum + level.price * level.size : sum
    }, 0)
    const buffered = notional * (1 - this.liquiditySafetyBpsValue / 10000)
    return Math.floor((buffered + Number.EPSILON) * 100) / 100
  }

  renderModalLimits() {
    const minimum = Number(this.selectedMinStake || 1)
    const maximum = this.selectedMaxStake === null ? Number.NaN : Number(this.selectedMaxStake)

    if (this.hasModalRangeTarget) {
      if (Number.isFinite(maximum) && maximum < minimum) {
        this.modalRangeTarget.textContent = this.t("temporarily_unavailable")
      } else {
        this.modalRangeTarget.textContent = Number.isFinite(maximum)
          ? this.t("current_range", { min: this.formatTp(minimum), max: this.formatTp(maximum) })
          : this.t("checking_limits")
      }
    }
    if (this.hasModalStakeInputTarget) {
      this.modalStakeInputTarget.min = String(minimum)
      if (Number.isFinite(maximum) && maximum >= minimum) {
        this.modalStakeInputTarget.max = String(maximum)
      } else {
        this.modalStakeInputTarget.removeAttribute("max")
      }
    }
    if (this.hasModalUseMaxTarget) {
      this.modalUseMaxTarget.disabled = !Number.isFinite(maximum) || maximum < minimum
    }
    this.updateModalSubmitAvailability()
  }

  renderModalValidation() {
    if (!this.hasModalStakeInputTarget) return

    const raw = this.modalStakeInputTarget.value
    const amount = Number(raw)
    let message = ""

    if (Number.isFinite(this.selectedMaxStake) && this.selectedMaxStake < this.selectedMinStake) {
      message = this.t("temporarily_unavailable")
    } else if (raw !== "" && (!Number.isFinite(amount) || amount < this.selectedMinStake)) {
      message = this.t("minimum_stake", { amount: this.formatTp(this.selectedMinStake) })
    } else if (raw !== "" && Number.isFinite(this.selectedMaxStake) && amount > this.selectedMaxStake) {
      message = this.t("maximum_stake", { amount: this.formatTp(this.selectedMaxStake) })
    }

    this.setModalLimitMessage(message)
    this.updateModalSubmitAvailability()
  }

  modalAmountValid() {
    if (!this.hasModalStakeInputTarget || !Number.isFinite(this.selectedMaxStake)) return false

    const amount = Number(this.modalStakeInputTarget.value)
    return Number.isFinite(amount) && amount >= this.selectedMinStake && amount <= this.selectedMaxStake
  }

  updateModalSubmitAvailability() {
    if (!this.hasModalSubmitTarget) return

    const pending = this.selectedForm?.dataset.pending === "true"
    this.modalSubmitTarget.disabled = this.quoteRefreshing || pending || !this.modalAmountValid()
  }

  setDisplayedOdds(form, odds) {
    const target = this.targetInside(this.displayedOddsTargets, form)
    if (target) target.value = String(odds)
  }

  async parseJson(response) {
    try {
      return await response.json()
    } catch (_error) {
      return {}
    }
  }

  setSubmitting(form, button, submitting) {
    form.dataset.pending = submitting ? "true" : "false"

    if (!button) return
    if (!button.dataset.defaultHtml) button.dataset.defaultHtml = button.innerHTML

    button.disabled = submitting
    button.innerHTML = submitting ? this.t("submitting") : button.dataset.defaultHtml
    if (!submitting && button === this.modalSubmitTarget) this.updateModalSubmitAvailability()
  }

  setFormMessage(form, message, style = "danger") {
    const fixture = form.closest("[data-polymarket-feed-target~='fixture']")
    const target = this.targetInside(this.messageTargets, fixture || form)
    if (!target) return

    target.textContent = message
    target.hidden = message.length === 0
    target.classList.toggle("is-success", style === "success")
  }

  setModalMessage(message, style = "danger") {
    if (!this.hasModalMessageTarget) return

    this.modalMessageTarget.textContent = message
    this.modalMessageTarget.hidden = message.length === 0
    this.modalMessageTarget.classList.toggle("is-success", style === "success")
  }

  setModalLimitMessage(message) {
    if (!this.hasModalLimitMessageTarget) return

    this.modalLimitMessageTarget.textContent = message
    this.modalLimitMessageTarget.hidden = message.length === 0
  }

  setAcceptedSummary(form, order) {
    if (!order) return

    const fixture = form.closest("[data-polymarket-feed-target~='fixture']")
    const target = this.targetInside(this.acceptedTargets, fixture)
    if (!target) return

    target.hidden = false
    target.replaceChildren(
      this.summaryLine(this.t("accepted"), `${order.outcome || ""}`),
      this.summaryLine(this.t("stake"), `${order.stake_display || order.stake_usdt || "0.00"} TP`),
      this.summaryLine(this.t("accepted_odds"), `x ${order.customer_odds_display || order.customer_odds || "-"}`),
      this.summaryLine(this.t("potential_payout"), `${order.potential_payout_display || order.potential_payout_usdt || "0.00"} TP`)
    )
  }

  summaryLine(label, value) {
    const row = document.createElement("div")
    row.textContent = `${label}: ${value}`
    return row
  }

  prependHistory(order) {
    if (!order || !this.hasHistoryTarget) return

    const row = document.createElement("div")
    row.className = "sportsbook-order-row"

    const main = document.createElement("div")
    main.className = "sportsbook-order-main"

    const text = document.createElement("div")
    const event = document.createElement("div")
    event.className = "sportsbook-order-event"
    event.textContent = order.event || ""
    const choice = document.createElement("div")
    choice.className = "sportsbook-order-choice"
    choice.textContent = order.outcome || ""
    text.append(event, choice)

    const status = document.createElement("span")
    status.className = "sportsbook-order-status is-open"
    status.textContent = this.t("awaiting_result")

    main.append(text, status)

    const meta = document.createElement("div")
    meta.className = "sportsbook-order-meta"
    ;[
      `${order.stake_display || order.stake_usdt || "0.00"} TP`,
      `x ${order.customer_odds_display || order.customer_odds || "-"}`,
      `${this.t("potential_payout")} ${order.potential_payout_display || order.potential_payout_usdt || "0.00"} TP`,
      this.t("just_now")
    ].forEach((value) => {
      const item = document.createElement("span")
      item.textContent = value
      meta.append(item)
    })

    row.append(main, meta)

    const empty = this.historyTarget.querySelector(".text-muted")
    if (empty) empty.remove()
    this.historyTarget.prepend(row)
  }

  updateBalances(balance) {
    if (!balance) return

    this.updateBalanceDisplay("tp", balance.total_tp_display)
    this.updateBalanceDisplay("gp", balance.total_gp_display)
    this.updateBalanceDisplay("locked-coupon", balance.locked_coupon_display)
  }

  updateBalanceDisplay(key, value) {
    if (value === undefined || value === null) return

    document.querySelectorAll(`[data-balance-display="${key}"]`).forEach((element) => {
      element.textContent = value
    })
  }

  renderModalOdds() {
    if (!this.hasModalOddsTarget) return

    const odds = Number(this.selectedOdds || 0)
    this.modalOddsTarget.textContent = Number.isFinite(odds) && odds > 0 ? odds.toFixed(2) : "-"
  }

  closeBetModal() {
    if (this.hasModalTarget && window.bootstrap?.Modal) {
      this.betModalInstance().hide()
    }
    this.resetBetModal()
  }

  resetBetModal() {
    this.stopQuoteRefresh()
    this.selectedForm = null
    this.selectedAssetId = null
    this.selectedOdds = null
    this.selectedMinStake = 1
    this.selectedMaxStake = null
    this.selectedNonBookMax = null
    this.setModalMessage("")
    this.setModalLimitMessage("")
    this.renderModalLimits()
    this.updateBetPreview()
  }

  betModalInstance() {
    if (!this.betModal) this.betModal = window.bootstrap.Modal.getOrCreateInstance(this.modalTarget)
    return this.betModal
  }

  fieldValue(form, field) {
    return form.querySelector(`[name="sports_bet[${field}]"]`)?.value || ""
  }

  formatTp(value) {
    const number = Number(value)
    if (!Number.isFinite(number)) return "0.00"

    return number.toLocaleString(this.hasLocaleValue ? this.localeValue : "en", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })
  }

  showToast(message, style = "success") {
    const container = document.getElementById("toasts")
    if (!container || message.length === 0) return

    const toast = document.createElement("div")
    toast.className = `toast align-items-center text-bg-${style} border-0`
    toast.setAttribute("role", "alert")
    toast.setAttribute("aria-live", "assertive")
    toast.setAttribute("aria-atomic", "true")

    const row = document.createElement("div")
    row.className = "d-flex"

    const body = document.createElement("div")
    body.className = "toast-body"
    body.textContent = message

    const close = document.createElement("button")
    close.type = "button"
    close.className = "btn-close btn-close-white me-2 m-auto"
    close.setAttribute("data-bs-dismiss", "toast")
    close.setAttribute("aria-label", this.t("close"))

    row.append(body, close)
    toast.append(row)
    container.append(toast)

    if (window.bootstrap?.Toast) {
      const instance = window.bootstrap.Toast.getOrCreateInstance(toast)
      toast.addEventListener("hidden.bs.toast", () => toast.remove())
      instance.show()
    } else {
      window.setTimeout(() => toast.remove(), 4000)
    }
  }

  targetInside(targets, root) {
    if (!root) return undefined

    return targets.find((target) => root.contains(target))
  }

  csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content || ""
  }

  t(key, replacements = {}) {
    let value = String(this.hasTranslationsValue ? this.translationsValue[key] || key : key)
    Object.entries(replacements).forEach(([name, replacement]) => {
      value = value.replaceAll(`{{${name}}}`, String(replacement))
    })
    return value
  }
}
